import React from "react";
import AttacedFileIcon from "../icons/AttachedFile";
import CancelIcon from "../icons/CancelIcon";
import { AttachedFile } from "../../types/chat";

interface AttachedFileItemProps {
  file: AttachedFile;
  onRemove: (fileName: string) => void;
}

export const AttachedFileItem: React.FC<AttachedFileItemProps> = ({
  file,
  onRemove,
}) => {
  const claimIngest = file.uploadResponse?.claim_ingest;
  const claimMatched = claimIngest?.matched === true;
  const claimMismatch = claimIngest?.mismatch === true;

  const getStatusColor = () => {
    if (file.isUploading || file.state === 'uploading') return "border-yellow-300 bg-yellow-50";
    if (claimMismatch) return "border-amber-300 bg-amber-50";
    if (claimMatched) return "border-blue-300 bg-blue-50";
    if (file.uploadResponse || file.state === 'uploaded') return "border-green-300 bg-green-50";
    if (file.state === 'error') return "border-red-300 bg-red-50";
    return "border-hex-gray-200 bg-hex-gray-50";
  };

  const getStatusText = () => {
    if (file.isUploading || file.state === 'uploading') return "Uploading...";
    if (claimMismatch) return "Different claim";
    if (claimMatched) return "Added to claim";
    if (file.uploadResponse || file.state === 'uploaded') return "Uploaded";
    if (file.state === 'error') return "Error";
    return "";
  };

  const claimIngestTooltip = claimMismatch
    ? claimIngest?.edi_claim_numbers?.length
      ? `This EDI file is for claim ${claimIngest.edi_claim_numbers.join(", ")}, but this chat is about claim ${
          claimIngest?.chat_claim_number || ""
        }. The claim data was still imported; remove the attachment if this was a mistake.`
      : `This EDI file has no valid claim number, so it was not linked to this chat's claim ${
          claimIngest?.chat_claim_number || ""
        }.`
    : claimMatched
      ? `Imported into claim ${
          claimIngest?.chat_claim_number || ""
        }. Find it in the claim's attachments — it won't be added to the chat as a file.`
      : undefined;

  // --- Click Handlers ---
  const handleRemoveClick = () => {
    onRemove(file.name);
  };

  // Don't allow removal once file is uploaded
  const canRemove = file.staticFile ? false : !file.isUploading;

  return (
    <div
      className={`flex min-w-0 max-w-full items-center justify-between text-sm font-manrope text-hex-gray-700 px-2 py-1 rounded-md border ${getStatusColor()}`}
      title={claimIngestTooltip || file.uploadResponse?.processing_error || undefined}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
        <AttacedFileIcon className="shrink-0" />
        <span className="block min-w-0 truncate text-md" title={file.name}>
          {file.name}
        </span>
        {getStatusText() && (
          <span className="textsm text-gray-500">{getStatusText()}</span>
        )}
      </div>
      {canRemove && (
        <button
          type="button"
          onClick={handleRemoveClick}
          className="text-red-500 text-xs hover:underline ml-2"
        >
          <CancelIcon />
        </button>
      )}
    </div>
  );
};

export default AttachedFileItem;
